// Service Worker registration for PWA support (Beta)

import debugx from "debugx";

const debug = debugx("bikedash:sw");

let deferredPrompt: any;
let installPromptCallback: ((show: boolean) => void) | null = null;
let updateAvailableCallback:
  | ((available: boolean, worker: ServiceWorker | null) => void)
  | null = null;

export function setInstallPromptCallback(callback: (show: boolean) => void) {
  installPromptCallback = callback;
}

export function setUpdateAvailableCallback(
  callback: (available: boolean, worker: ServiceWorker | null) => void,
) {
  updateAvailableCallback = callback;
}

export async function triggerInstallPrompt(): Promise<
  "accepted" | "dismissed" | "unavailable"
> {
  if (!deferredPrompt) {
    debug("Install prompt unavailable - no deferred prompt");
    return "unavailable";
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  debug("Install prompt outcome: %s", outcome);
  deferredPrompt = null;

  // Hide the install callback after user makes a choice
  if (installPromptCallback) {
    installPromptCallback(false);
  }

  return outcome;
}

export function register() {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").then(
        (registration) => {
          debug("ServiceWorker registration successful");

          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  // New service worker available
                  debug("ServiceWorker update available");
                  if (updateAvailableCallback) {
                    updateAvailableCallback(true, newWorker);
                  }
                }
              });
            }
          });
        },
        (error) => {
          debug("ServiceWorker registration failed: %O", error);
        },
      );

      // Clean up old bundles after successful load
      cleanupOldBundles();
    });
  }

  // Listen for beforeinstallprompt event
  window.addEventListener("beforeinstallprompt", (e) => {
    debug("beforeinstallprompt event fired");
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    // Update UI to notify the user they can install the PWA
    if (installPromptCallback) {
      installPromptCallback(true);
    }
  });

  // Listen for app installed event
  window.addEventListener("appinstalled", () => {
    debug("PWA was installed");
    deferredPrompt = null;
    if (installPromptCallback) {
      installPromptCallback(false);
    }
  });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        debug("ServiceWorker unregister error: %O", error.message);
      });
  }
}

/**
 * Clean up old bundles that aren't in the current asset manifest
 * Called after successful app load to remove outdated cached bundles
 */
async function cleanupOldBundles(): Promise<void> {
  if (!("caches" in window)) return;

  try {
    // Get current asset manifest
    const manifestElement = document.getElementById("asset-manifest");
    if (!manifestElement) {
      debug("No asset manifest found, skipping cleanup");
      return;
    }

    const manifest = JSON.parse(manifestElement.textContent || "{}");
    const currentAssets = new Set(manifest.assets || []);

    debug("Current assets in manifest: %d", currentAssets.size);

    // Get all bundle caches
    const cacheNames = await caches.keys();
    const bundleCaches = cacheNames.filter((name) => name.includes("bundles"));

    for (const cacheName of bundleCaches) {
      const cache = await caches.open(cacheName);
      const cachedRequests = await cache.keys();

      let deletedCount = 0;
      for (const request of cachedRequests) {
        const url = new URL(request.url);
        const pathname = url.pathname;

        // Delete if not in current manifest
        if (!currentAssets.has(pathname)) {
          await cache.delete(request);
          deletedCount++;
          debug("Deleted old bundle: %s", pathname);
        }
      }

      if (deletedCount > 0) {
        debug("Cleaned up %d old bundles from %s", deletedCount, cacheName);
      }
    }
  } catch (error) {
    debug("Error cleaning up old bundles: %O", error);
  }
}

/**
 * Clear caches in background WITHOUT reloading
 * Used for auto-update - prepares for fresh content on next reload
 */
export async function prepareUpdate(): Promise<void> {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    debug("Preparing update - clearing caches in background");

    return new Promise((resolve) => {
      const messageHandler = (event: MessageEvent) => {
        if (event.data && event.data.type === "CACHES_CLEARED") {
          debug("Caches cleared, update prepared");
          navigator.serviceWorker.removeEventListener(
            "message",
            messageHandler,
          );
          resolve();
        }
      };

      navigator.serviceWorker.addEventListener("message", messageHandler);

      // Send CLEAR_CACHES to current service worker
      navigator.serviceWorker.controller!.postMessage({ type: "CLEAR_CACHES" });

      // Timeout fallback
      setTimeout(() => {
        navigator.serviceWorker.removeEventListener("message", messageHandler);
        resolve();
      }, 2000);
    });
  }
}

/**
 * Clear all service worker caches and reload the page to get fresh content
 * Only call this when the user explicitly requests an update
 */
export async function clearCachesAndReload(): Promise<void> {
  if ("serviceWorker" in navigator) {
    debug("Requesting service worker to clear all caches and update");

    // First, check if there's a waiting service worker and activate it
    const registration = await navigator.serviceWorker.ready;
    if (registration.waiting) {
      debug("Activating waiting service worker");
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }

    // Now clear caches if we have a controller
    if (navigator.serviceWorker.controller) {
      return new Promise((resolve) => {
        // Listen for cache cleared confirmation
        const messageHandler = (event: MessageEvent) => {
          if (event.data && event.data.type === "CACHES_CLEARED") {
            debug("Service worker caches cleared, reloading page");
            navigator.serviceWorker.removeEventListener(
              "message",
              messageHandler,
            );
            resolve();
            // Reload with cache bypass
            window.location.reload();
          }
        };

        navigator.serviceWorker.addEventListener("message", messageHandler);

        // Send message to service worker to clear caches
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: "CLEAR_CACHES",
          });
        }

        // Fallback: reload anyway after 2 seconds if no response
        setTimeout(() => {
          debug("Cache clear timeout, reloading anyway");
          navigator.serviceWorker.removeEventListener(
            "message",
            messageHandler,
          );
          resolve();
          window.location.reload();
        }, 2000);
      });
    } else {
      // No controller yet, just reload
      debug("No service worker controller, doing hard reload");
      window.location.reload();
    }
  } else {
    // No service worker support, just reload
    debug("No service worker support, doing hard reload");
    window.location.reload();
  }
}
